import {
    useDeleteFulfillmentProduct,
    usePatchFulfillmentProductConfiguration,
    usePostFulfillmentProduct,
} from '@/clients/fulfillment';
import { getFulfillmentProductGroupsByOrderIdQueryKey } from '@/clients/fulfillment/useFulfillmentProductGroup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFulfillmentContext } from '../context/FulfillmentContext';
import {
    useDeleteFulfillmentQuote,
    usePatchFulfillmentQuote,
    usePostFulfillmentQuote,
} from '@/CustomRequestProductChannel/clients/CustomRequestClient';
import {
    CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA,
    PostFulfillmentProductRequest,
    PostQuoteFulfillmentRequestSchema,
} from '@schuettflix/interfaces';
import { z } from 'zod';

export const useFulfillmentMutations = () => {
    const queryClient = useQueryClient();

    const {
        productGroup,
        contextSource,
        addFulfillmentProduct,
        removeFulfillmentProduct,
        addFulfillmentQuote,
        updateFulfillmentQuoteByOrderProductId,
        removeFulfillmentQuote,
    } = useFulfillmentContext();

    //////////////////////////////////////////
    // Product Mutations                    //
    //////////////////////////////////////////

    const invalidateProductRelatedData = () => {
        if (contextSource === 'position') return;
        void queryClient.invalidateQueries({
            queryKey: getFulfillmentProductGroupsByOrderIdQueryKey(productGroup.orderId),
        });
    };

    const { postFulfillmentProduct } = usePostFulfillmentProduct();
    const postFulfillmentProductMutation = useMutation({
        mutationKey: [contextSource, 'post-fulfillment-product'],
        mutationFn: async (fulfillmentProduct: PostFulfillmentProductRequest) => {
            if (contextSource === 'position') {
                return addFulfillmentProduct({
                    ...fulfillmentProduct,
                    fulfillmentProductConfiguration: {
                        showZeroPurchasePrice: false,
                        showZeroSalesPrice: false,
                    },
                });
            }

            return await postFulfillmentProduct(fulfillmentProduct);
        },
        onSuccess: invalidateProductRelatedData,
    });

    const { patchFulfillmentProductConfiguration } = usePatchFulfillmentProductConfiguration();
    const patchFulfillmentProductConfigurationMutation = useMutation({
        mutationKey: ['patch-fulfillment-product-configuration'],
        mutationFn: patchFulfillmentProductConfiguration,
        onSuccess: invalidateProductRelatedData,
    });

    const { deleteFulfillmentProduct } = useDeleteFulfillmentProduct();
    const deleteFulfillmentProductMutation = useMutation({
        mutationKey: ['delete-fulfillment-product'],
        mutationFn: async (...args: Parameters<typeof deleteFulfillmentProduct>) => {
            if (contextSource === 'position') {
                const [{ fulfillmentProductId }] = args;
                return removeFulfillmentProduct(fulfillmentProductId);
            }
            return await deleteFulfillmentProduct(...args);
        },
        onSuccess: invalidateProductRelatedData,
    });

    //////////////////////////////////////////
    // Quote Mutations                      //
    //////////////////////////////////////////

    const invalidateQuoteRelatedData = () => {
        if (contextSource === 'position') return;
        void queryClient.invalidateQueries({ queryKey: ['quotes-by-order', productGroup.orderId] });
        void queryClient.invalidateQueries({
            queryKey: ['fulfillment-summary', productGroup.orderId],
        });
    };

    const { postFulfillmentQuote } = usePostFulfillmentQuote();
    const postFulfillmentQuoteMutation = useMutation({
        mutationKey: ['order-product-group', 'product-quote'],
        mutationFn: async (quote: Omit<z.input<typeof PostQuoteFulfillmentRequestSchema>, 'orderId'>) => {
            if (contextSource === 'position') {
                return addFulfillmentQuote({
                    ...quote,
                    productType: CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA.parse(quote.productType),
                    name: quote.payload.name,
                    freightType: 'freightType' in quote.payload ? quote.payload.freightType : undefined,
                    disposalPurpose: 'disposalPurpose' in quote.payload ? quote.payload.disposalPurpose : undefined,
                });
            }
            return await postFulfillmentQuote({
                ...quote,
                orderId: productGroup.orderId,
                productType: CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA.parse(quote.productType),
            });
        },
        onSuccess: invalidateQuoteRelatedData,
    });

    const { patchFulfillmentQuote } = usePatchFulfillmentQuote();
    const patchFulfillmentQuoteMutation = useMutation({
        mutationKey: ['order-product-group', 'product-quote'],
        mutationFn: async (...args: Parameters<typeof patchFulfillmentQuote>) => {
            if (contextSource === 'position') {
                const [{ body: quote, productId }] = args;
                return updateFulfillmentQuoteByOrderProductId(productId, quote);
            }
            return await patchFulfillmentQuote(...args);
        },
        onSuccess: invalidateQuoteRelatedData,
    });

    const { deleteFulfillmentQuote } = useDeleteFulfillmentQuote();
    const deleteFulfillmentQuoteMutation = useMutation({
        mutationKey: ['order-product-group', 'product-quote'],
        mutationFn: async (...args: Parameters<typeof deleteFulfillmentQuote>) => {
            if (contextSource === 'position') {
                const [{ quoteId }] = args;
                return removeFulfillmentQuote(quoteId);
            }
            return await deleteFulfillmentQuote(...args);
        },
        onSuccess: invalidateQuoteRelatedData,
    });

    return {
        // product mutations
        postFulfillmentProductMutation,
        patchFulfillmentProductConfigurationMutation,
        deleteFulfillmentProductMutation,

        // quote mutations
        postFulfillmentQuoteMutation,
        patchFulfillmentQuoteMutation,
        deleteFulfillmentQuoteMutation,
    };
};
